// Las preguntas frecuentes de la app.
//
// Cada número sale de la constante que usa la regla de verdad (el Elo, la
// tabla del mes, los logros...). Así, si mañana se cambia un mínimo o un
// plazo, la ayuda cambia sola y nunca le cuenta al grupo algo que ya no es.
// Las pocas que viven solo en el servidor están copiadas en DEL_SERVIDOR, y
// un test (preguntas.test.ts) falla si alguna deja de coincidir.

import type { ReactNode } from 'react';
import { AyudaInstalar } from '../pwa/AyudaInstalar';
import {
  ELO_INICIAL,
  K_CALIBRACION,
  K_NORMAL,
  PARTIDOS_CALIBRACION,
  PARTIDOS_PARA_CONFIAR,
  factorMargen,
  probabilidadEsperada,
} from '../nucleo/elo';
import { DIAS_HACIA_ATRAS, DIAS_PARA_AUTOCONFIRMAR, HORA_DE_CIERRE } from '../nucleo/fechas';
import { ESCALA, MESES_QUE_CUENTAN, PUNTOS_RESTO, RANKING_PUBLICO } from '../nucleo/puntos';
import {
  MES_FLOJO,
  MINIMO_MES_FLOJO,
  MINIMO_PARTIDOS,
  PUNTOS_POR_GANADO,
  PUNTOS_POR_PERDIDO,
} from '../nucleo/mensual';
import { DIAS_EN_CARRERA } from '../nucleo/ranking';
import { MINIMO_PAREJA } from '../nucleo/parejas';
import { MINIMO_PARA_OPINAR } from '../nucleo/perfil';
import {
  APLASTANTES_APLANADORA,
  CHANCE_MATAGIGANTES,
  COMPANEROS_CAMALEON,
  GAMES_APLASTANTE,
  MINIMO_NOCHE_PERFECTA,
  NIVELES_TIEBREAK,
  RACHA_IMBATIBLE,
  RACHA_MALA,
  VICTORIAS_BESTIA,
  VICTORIAS_DUPLA,
} from '../nucleo/logros';

/** Lo que solo sabe el servidor. preguntas.test.ts vigila que siga siendo así. */
export const DEL_SERVIDOR = {
  /** Compartido con la carga y el Worker desde nucleo/fechas.ts. */
  diasParaCargarTarde: DIAS_HACIA_ATRAS,
  /** src/worker/seguridad.ts: FALLOS_PARA_BLOQUEAR y MINUTOS_BLOQUEADO */
  fallosDePin: 5,
  minutosBloqueado: 15,
  /** src/worker/seguridad.ts: DIAS_DE_SESION */
  diasDeSesion: 90,
};

export interface Pregunta {
  pregunta: string;
  respuesta: ReactNode;
}

export interface Seccion {
  id: string;
  titulo: string;
  preguntas: Pregunta[];
}

const dias = (n: number) => (n === 1 ? '1 día' : `${n} días`);

// Ejemplos del Elo calculados con la fórmula de verdad, no escritos a mano.
const kHoy = K_NORMAL;
const parejo = Math.round(kHoy * factorMargen(6, 4) * (1 - 0.5));
const chanceFavorito = probabilidadEsperada(1100, 900);
const ganaFavorito = Math.round(kHoy * factorMargen(6, 4) * (1 - chanceFavorito));
const ganaElOtro = Math.round(kHoy * factorMargen(6, 4) * (1 - (1 - chanceFavorito)));
const porcentaje = (x: number) => `${Math.round(x * 100)}%`;
const [bronce, plata, oro] = NIVELES_TIEBREAK;

export const SECCIONES: Seccion[] = [
  {
    id: 'uso',
    titulo: 'Usar la app',
    preguntas: [
      {
        pregunta: '¿Cómo lo instalo?',
        respuesta: <AyudaInstalar />,
      },
      {
        pregunta: '¿Cómo cargo un partido?',
        respuesta: (
          <>
            <p>
              En <b>➕ Cargar</b>: elegís la fecha, las dos parejas y los games del set. Lo carga
              cualquiera de los cuatro que jugaron, una sola vez. Queda <b>pendiente</b> hasta que
              lo confirma alguien del equipo rival.
            </p>
            <p>
              Lo normal es cargarlo <b>la misma noche</b>. Se pueden cargar partidos de hasta{' '}
              {dias(DEL_SERVIDOR.diasParaCargarTarde)} atrás, siempre que su mes no haya cerrado
              (ver <i>Las tablas</i>); si no, se lo pedís a un administrador.
            </p>
          </>
        ),
      },
      {
        pregunta: '¿Por qué tiene que confirmarlo el rival?',
        respuesta: (
          <p>
            Para que nadie pueda anotarse partidos que no jugó, o un resultado que no fue. El que
            lo cargó no lo puede confirmar, y su compañero tampoco: tiene que ser alguien del otro
            lado. Mientras tanto <b>ya cuenta</b> en las tablas, para que nadie tenga que esperar
            a ver cómo quedó; si el rival lo rechaza, sale de la cuenta y todo se rehace.
          </p>
        ),
      },
      {
        pregunta: '¿Y si el rival no lo confirma nunca?',
        respuesta: (
          <p>
            A {dias(DIAS_PARA_AUTOCONFIRMAR)} de cargado, el sistema lo da por bueno solo, y queda
            marcado como <i>“confirmado por inactividad”</i>. También se confirma solo si el mes
            cierra antes de ese plazo. Además, un administrador lo puede
            confirmar antes, y ahí queda marcado <i>“confirmado por admin”</i>: sirve para cerrar
            la noche el mismo día sin esperar a cada rival.
          </p>
        ),
      },
      {
        pregunta: 'Cargué mal el resultado, ¿qué hago?',
        respuesta: (
          <>
            <p>
              Si todavía está pendiente y lo que está mal es el <b>marcador</b>, en Inicio aparece
              <b> Corregir</b>. Lo puede usar el que lo cargó o su compañero; el rival lo sigue
              confirmando igual, y en el partido queda anotado quién lo corrigió y de qué a qué.
            </p>
            <p>
              Si están mal los <b>jugadores</b> o la <b>fecha</b>, que el rival lo rechace
              (<i>“No, está mal”</i>) y se carga de nuevo. Si ya se confirmó, lo anula un
              administrador: no se borra, queda tachado en el historial con quién lo anuló y por
              qué, y la tabla se rehace sola.
            </p>
          </>
        ),
      },
      {
        pregunta: 'Dos cargamos el mismo partido, ¿se duplica?',
        respuesta: (
          <p>
            No sin avisar. Si el mismo día ya hay uno con las mismas parejas y el mismo resultado,
            la app pregunta si es el mismo o si es otro partido (en un americano puede pasar que
            se repita). Si decís que es el mismo, no se carga nada.
          </p>
        ),
      },
      {
        pregunta: '¿Cómo se carga un set que se definió en tiebreak?',
        respuesta: (
          <p>
            Marcás el empate (5-5 o 6-6) y la app pregunta quién ganó el tiebreak: queda como 6-5
            o 7-6, porque el tiebreak vale como el game que define el set. Si querés, anotás el
            tanteo (7-5, 8-6…): se muestra, pero no cambia ningún número.
          </p>
        ),
      },
      {
        pregunta: '¿Dónde veo todos los partidos, y quién cargó cada uno?',
        respuesta: (
          <p>
            En Inicio, <b>Ver todos los partidos</b>: todos los del mes, por día, incluidos los
            rechazados y anulados. Tocando las parejas de cualquier partido se ve quién lo cargó,
            a qué hora, y todo lo que le pasó después (quién lo confirmó, corrigió, rechazó o
            anuló).
          </p>
        ),
      },
      {
        pregunta: '¿Qué es la placa de la noche?',
        respuesta: (
          <p>
            Una imagen con los resultados de la noche, cómo se movió la tabla, la figura (el que
            más ganó) y los logros de esa noche, para mandar al grupo de WhatsApp. Está en Inicio
            (📸 Placa de la noche) y en cada día del historial. Primero se arma y se ve; después,
            <b> Mandar al grupo</b>.
          </p>
        ),
      },
      {
        pregunta: 'Me olvidé el PIN, o me equivoqué varias veces',
        respuesta: (
          <p>
            Después de {DEL_SERVIDOR.fallosDePin} intentos equivocados, esa cuenta queda
            bloqueada {DEL_SERVIDOR.minutosBloqueado} minutos. Si no te acordás, pedile a un
            administrador un PIN nuevo: te pasa uno provisorio y la app te pide que elijas uno
            tuyo al entrar. Para cambiarlo cuando quieras: tocá tu nombre, arriba, y <i>Cambiar mi
            PIN</i>.
          </p>
        ),
      },
      {
        pregunta: '¿Cada cuánto tengo que volver a entrar?',
        respuesta: (
          <p>
            La sesión dura {dias(DEL_SERVIDOR.diasDeSesion)} y se renueva sola mientras la uses.
            Conviene <b>instalar la app</b> en el celular (la tarjeta de instalar aparece abajo):
            queda el ícono y se abre directo. En iPhone, la app instalada tiene su propia sesión,
            así que conviene instalarla antes de poner el PIN.
          </p>
        ),
      },
      {
        pregunta: '¿Anda sin señal?',
        respuesta: (
          <p>
            La app abre igual y avisa que no hay conexión, pero para cargar, confirmar o ver la
            tabla hace falta señal: el ranking es uno solo para todos y se lee siempre del
            servidor, así dos celulares nunca muestran tablas distintas.
          </p>
        ),
      },
    ],
  },
  {
    id: 'tablas',
    titulo: 'Las tablas',
    preguntas: [
      {
        pregunta: '¿Cómo se calculan los puntos de Nivel?',
        respuesta: (
          <>
            <p>
              Es un sistema <b>tipo ajedrez</b> (Elo). Todos arrancaron en {ELO_INICIAL}. En cada
              partido se compara el puntaje de las dos parejas (cada pareja vale el promedio de
              sus dos jugadores) y se calcula qué tan probable era que ganara cada una. Después:
            </p>
            <ul>
              <li>Si ganás siendo favorito, sumás poco. Si ganás siendo el de abajo, sumás mucho.</li>
              <li>Lo que suma la pareja que gana es exactamente lo que resta la que pierde.</li>
              <li>
                Los dos de la pareja se llevan lo mismo, porque en un americano se juega con
                distinta gente cada vez y a la larga se compensa.
              </li>
              <li>
                El resultado pesa: ganar 6-0 mueve más que ganar 6-5. Pero ganar siempre suma: un
                6-4 nunca te baja.
              </li>
            </ul>
            <p>
              Ejemplos con los números de hoy: entre dos parejas parejas, ganar 6-4 da{' '}
              <b>+{parejo}</b>. Si una pareja de 1100 le gana 6-4 a una de 900 (tenía{' '}
              {porcentaje(chanceFavorito)} de chances), suma <b>+{ganaFavorito}</b>; si gana la de
              900, suma <b>+{ganaElOtro}</b>.
            </p>
            <p>
              Los primeros {PARTIDOS_CALIBRACION} partidos del grupo movieron más ({K_CALIBRACION}{' '}
              en vez de {K_NORMAL}) para que la tabla se acomodara rápido.
            </p>
          </>
        ),
      },
      {
        pregunta: '¿Qué quiere decir “provisorio”?',
        respuesta: (
          <p>
            Que ese jugador tiene menos de {PARTIDOS_PARA_CONFIAR} partidos: su puntaje todavía se
            está acomodando y puede moverse bastante. No cambia ninguna cuenta, es solo un aviso.
          </p>
        ),
      },
      {
        pregunta: '¿Por qué algunos aparecen abajo, sin puesto?',
        respuesta: (
          <p>
            Porque no jugaron ningún partido confirmado en los últimos {dias(DIAS_EN_CARRERA)}.
            Como todos arrancan en {ELO_INICIAL}, el que casi no viene quedaría en la mitad de la
            tabla, arriba de gente que juega siempre. No pierden puntos: vuelven a la tabla con su
            puntaje en cuanto jueguen de nuevo.
          </p>
        ),
      },
      {
        pregunta: '¿Cómo funciona la tabla del mes?',
        respuesta: (
          <>
            <p>
              Se reinicia cada mes y va por puntos: <b>{PUNTOS_POR_GANADO} por partido ganado</b> y{' '}
              <b>{PUNTOS_POR_PERDIDO} por partido perdido</b> (el {PUNTOS_POR_PERDIDO} premia venir a
              jugar). Si dos empatan en puntos, desempata el porcentaje de victorias, después la
              diferencia de games, y por último cuánto subieron en Nivel.
            </p>
            <p>
              Para pelear el campeonato hay que jugar por lo menos {MINIMO_PARTIDOS} partidos en el
              mes ({MINIMO_MES_FLOJO} si el mes tuvo menos de {MES_FLOJO} partidos en total). Los
              que no llegan aparecen igual, atenuados.
            </p>
          </>
        ),
      },
      {
        pregunta: '¿Por qué el campeón del mes va por puntos y no por Nivel?',
        respuesta: (
          <p>
            Porque en Nivel el mejor del grupo es favorito en casi todos sus partidos y,
            ganando todo, sube poco. Si el campeón saliera de ahí, casi nunca ganaría. Por eso el
            mes reparte tres reconocimientos distintos: el <b>campeón</b> (más puntos), <b>el que
            más subió</b> (el que más superó lo que se esperaba de él) y <b>el más presente</b>{' '}
            (el que más jugó).
          </p>
        ),
      },
      // En votacion (RANKING_PUBLICO): la pregunta sale cuando se publique.
      ...(RANKING_PUBLICO
        ? [
      {
        pregunta: '¿Cómo funciona el Ranking?',
        respuesta: (
          <>
            <p>
              Como el del ATP o Premier Padel: <b>cada mes es un torneo</b>, y al cerrar reparte
              puntos según el puesto en la tabla del mes: {ESCALA[0]} al 1º, {ESCALA[1]} al 2º,{' '}
              {ESCALA[2]} al 3º, {ESCALA[3]} al 4º, {ESCALA[4]} del 5º al 8º, {ESCALA[8]} del 9º
              al 12º y {PUNTOS_RESTO} al resto.
            </p>
            <p className="mt-2">
              Si dos terminan con los mismos puntos del mes, se reparten el promedio de los dos
              puestos. El que no llegó al mínimo de partidos para la corona se lleva{' '}
              {PUNTOS_RESTO}, sin importar cuánto ganó: para pelear el puesto hay que venir.
            </p>
            <p className="mt-2">
              Suman los últimos <b>{MESES_QUE_CUENTAN} meses</b>. Cuando cierra un mes, se caen
              los puntos del mismo mes del año anterior: hay que defenderlos. La columna{' '}
              <i>En juego</i> muestra lo que sumaría cada uno si el mes cerrara hoy.
            </p>
          </>
        ),
      },
          ]
        : []),
      {
        pregunta: '¿Cuándo es definitivo el campeón del mes?',
        respuesta: (
          <p>
            El mes cierra el <b>1 del mes siguiente a las {HORA_DE_CIERRE} de la mañana</b>. Esas
            horas de margen son para cargar los partidos del último día del mes, cuando ese día se
            juega: esa noche hay que cargarlos sí o sí. Al cerrar, los partidos del mes que
            seguían pendientes se confirman solos, y el campeón queda definitivo. Desde ahí el mes
            no acepta partidos nuevos: solo lo puede tocar un administrador.
          </p>
        ),
      },
      {
        pregunta: '¿Cómo se arma la tabla de parejas?',
        respuesta: (
          <p>
            Entran las parejas que jugaron {MINIMO_PAREJA} partidos o más juntas, ordenadas por
            cuánto ganan. Con pocos partidos pesa menos: se cuenta como si cada pareja arrancara
            con un ganado y un perdido, así dos de dos no le gana a nueve de diez. Va por
            resultados, como el campeón del mes.
          </p>
        ),
      },
      {
        pregunta: '¿Qué es el “vs. esperado” del perfil?',
        respuesta: (
          <p>
            En <i>Con quién rinde</i>, compara lo que ganó un jugador con cada compañero contra lo
            que se esperaba de esos dos según el puntaje de los cuatro en cada partido. Así, jugar
            con el mejor del grupo no infla el número: sube el que rinde <b>más</b> de lo que
            tocaba. Con menos de {MINIMO_PARA_OPINAR} partidos juntos casi no se mueve del cero, a
            propósito.
          </p>
        ),
      },
      {
        pregunta: 'Anularon un partido viejo, ¿se arregla la tabla?',
        respuesta: (
          <p>
            Sí, sola. La app guarda solo los partidos; los puntos, las tablas y los logros se
            recalculan desde el primer partido cada vez que algo cambia. Por eso anular un partido
            de hace tres semanas deja todo exactamente como si nunca se hubiera jugado.
          </p>
        ),
      },
    ],
  },
  {
    id: 'logros',
    titulo: 'Los logros',
    preguntas: [
      {
        pregunta: '¿Cómo se consiguen los logros?',
        respuesta: (
          <p>
            Los automáticos los calcula la app a partir de los partidos que cuentan, incluidos los pendientes, no hay que reclamar
            nada. Si se anula el partido donde alguien consiguió uno, el logro se mueve o
            desaparece. Los ves en el perfil de cada uno, en <i>Logros</i>: los conseguidos a
            color, con cuándo, con quién o qué mes; los que faltan, apagados.
          </p>
        ),
      },
      {
        pregunta: '¿Qué son los logros del grupo?',
        respuesta: (
          <p>
            Cada administrador puede crear logros automáticos con reglas claras o entregar un
            reconocimiento manual explicando el motivo. Los manuales quedan marcados como tales:
            no cambian el Nivel ni los puntos y no desaparecen cuando se recalculan los partidos.
            Si se entregó uno por error, el administrador lo revoca dejando el motivo.
          </p>
        ),
      },
      {
        pregunta: 'Los de partidos y rachas',
        respuesta: (
          <ul>
            <li><b>🥇 Primera victoria</b> — ganar un partido.</li>
            <li><b>🔥 En racha</b>, <b>🚀 Imparable</b> y <b>🛡️ Imbatible</b> — 3, 5 y {RACHA_IMBATIBLE} partidos ganados seguidos.</li>
            <li><b>🥯 Paliza</b> — ganar un set 6-0.</li>
            <li>
              <b>🗡️ Matagigantes</b> — ganar un partido en el que, según los puntajes, tenían menos
              de {porcentaje(CHANCE_MATAGIGANTES)} de chances.
            </li>
            <li><b>🎯 Al líder</b> — ganarle a la pareja donde jugaba el 1º de Nivel.</li>
            <li>
              <b>🎲 Rey del tiebreak</b> — sets ganados en tiebreak, por niveles: 🥉 {bronce}, 🥈{' '}
              {plata} y 🥇 {oro}.
            </li>
            <li><b>🎾 Habitué</b>, <b>🏟️ De la casa</b> y <b>🏛️ Leyenda del club</b> — 10, 25 y 50 partidos jugados.</li>
          </ul>
        ),
      },
      {
        pregunta: 'Los de la noche',
        respuesta: (
          <>
            <ul>
              <li>
                <b>✨ Noche perfecta</b> — ganar todos los partidos que jugaste esa noche, con{' '}
                {MINIMO_NOCHE_PERFECTA} como mínimo.
              </li>
              <li>
                <b>🚜 Aplanadora</b> — en una noche, ganar {APLASTANTES_APLANADORA} partidos o más
                dejando al rival en {GAMES_APLASTANTE} games o menos. Si esa noche también
                perdiste alguno, cuenta igual.
              </li>
            </ul>
            <p>Estos se pueden ganar muchas veces: el perfil muestra cuántas (×N) y cuándo.</p>
          </>
        ),
      },
      {
        pregunta: 'Los que se ganan con otros',
        respuesta: (
          <ul>
            <li>
              <b>🤝 Dupla de oro</b> — {VICTORIAS_DUPLA} victorias con el mismo compañero. La ganan
              los dos, y dice con quién. Se puede tener con varios.
            </li>
            <li><b>🦎 Camaleón</b> — ganar con {COMPANEROS_CAMALEON} compañeros distintos.</li>
            <li>
              <b>😈 Bestia negra</b> — ganarle {VICTORIAS_BESTIA} veces al mismo rival, y además
              tener más ganados que perdidos contra él. Dice de quién, y solo lo ve el que lo ganó.
              Una vez conseguido no se pierde, aunque después el otro dé vuelta el cara a cara.
            </li>
          </ul>
        ),
      },
      {
        pregunta: 'Los del mes y del año',
        respuesta: (
          <>
            <p>Se entregan cuando el mes se cierra, y dicen qué mes:</p>
            <ul>
              <li><b>👑 Campeón del mes</b> — el 1º de la tabla del mes.</li>
              <li>
                <b>⛰️ En la cima</b> — terminar el mes 1º en Nivel (entre los que jugaron
                en los {dias(DIAS_EN_CARRERA)} anteriores).
              </li>
              <li><b>📈 El que más subió</b> y <b>🙋 El más presente</b> — los otros dos reconocimientos del mes.</li>
              <li>
                <b>📅 Siempre presente</b> — jugar todas las noches de partido del mes. Una noche
                que el club no les dio (por un torneo, por ejemplo) no cuenta: nadie falta a una
                noche que no hubo.
              </li>
            </ul>
            <p>
              <b>🏆 Campeón del año</b> — el que más campeonatos del mes ganó de enero a diciembre.
              Si dos empatan, gana el que sumó más puntos en el año. Se entrega cuando cierra
              diciembre. 2026 cuenta, aunque la app arrancó en septiembre.
            </p>
            <p>Si hay empate en cualquiera de estos, lo ganan todos los empatados.</p>
          </>
        ),
      },
      {
        pregunta: '¿Qué es “El 1”?',
        respuesta: (
          <>
            <p>
              <b>👑 “El 1”</b> es el que tiene <b>más campeonatos del mes</b> en toda la historia. No
              es un logro que se guarda: es un título que <b>rota</b>. Cuando otro lo supera, pasa
              a él.
            </p>
            <p>
              Lo tiene uno solo: si dos quedan empatados arriba, queda <b>vacante</b> hasta que
              alguno supere al otro. Cambia de dueño cuando cierra un mes. El que lo tiene lleva
              una coronita al lado del nombre en las tablas, y la placa de la primera noche después
              del cambio lo anuncia. En los logros de cada uno queda desde qué mes hasta qué mes
              fue “El 1”.
            </p>
          </>
        ),
      },
      {
        pregunta: '¿Y “Los otros logros”?',
        respuesta: (
          <>
            <ul>
              <li><b>🍩 Rosca</b> — perder un set 6-0.</li>
              <li><b>🌧️ Mala racha</b> — {RACHA_MALA} partidos perdidos seguidos.</li>
            </ul>
            <p>
              Son de humor: van aparte en el perfil y salen en la placa. Se pueden tener varias
              veces; para una Mala racha nueva hay que cortar la anterior ganando uno.
            </p>
          </>
        ),
      },
    ],
  },
];
